import { useI18n } from "@core/i18n/i18n";
import { patientManager, getStartedManager } from "@core/service-container";
import { FunctionalStatus, functionalStatuses } from "@domain/medical-condition/medical-condition";
import { useFormatMedicalCondition } from "@domain/medical-condition/use-medical-condition";
import { PendingPatient } from "@domain/patient/patient";
import { sendAnalyticsEventPatientCreated } from "@domain/patient/patient-analytics";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { ErrorMessage } from "@ui/components/common/error-message";
import { TextInput } from "@ui/components/inputs/text-input";
import { row, stack } from "@ui/style";
import { isNotNil } from "@utils/assert";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Panel } from "@ui/components/common/panel";

interface FormData {
	firstName: string;
	lastName: string;
	functionalStatus?: FunctionalStatus;
	dataConsent: boolean;
}

export const CreatePendingPatientForm: React.FC<{
	onPendingPatientCreated?: (pendingPatient: PendingPatient) => void;
	onBack: () => void;
}> = ({ onPendingPatientCreated, onBack }) => {
	const { format } = useI18n();
	const { formatFunctionalStatus } = useFormatMedicalCondition();
	const [error, setError] = useState("");
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const { control, handleSubmit } = useForm<FormData>({
		defaultValues: { functionalStatus: undefined },
		shouldFocusError: false,
	});

	const createPendingPatient = async (formData: FormData) => {
		try {
			setError("");
			const pendingPatient = await patientManager.createPendingPatient(formData);
			await getStartedManager.tryMarkPatientsStepAsCompleted();
			sendAnalyticsEventPatientCreated(pendingPatient.analyticsId, isNotNil(formData.functionalStatus));
			onPendingPatientCreated?.(pendingPatient);
		} catch (e) {
			console.error(e);
			setError(e instanceof Error ? e.message : "");
			setLoadingSubmit(false);
		}
	};

	return (
		<Form
			onSubmit={handleSubmit(data => {
				setLoadingSubmit(true);
				createPendingPatient(data);
			})}
		>
			<FormContainer>
				<FormField>
					<label className="p-size-m">{format("home.pendingPatientDialog.form.firstName.label")}</label>
					<Controller
						name="firstName"
						control={control}
						rules={{
							required: format("global.form.field.requiredError"),
						}}
						render={({ field, fieldState }) => (
							<>
								<TextInput
									{...field}
									data-test={e2eTestId.createPatientDialog.firstNameTextInput}
									error={isNotNil(fieldState.error)}
									placeholder={format("home.pendingPatientDialog.form.firstName.label")}
									autoCapitalize="words"
								/>
								<ErrorMessage>{error || fieldState.error?.message}</ErrorMessage>
							</>
						)}
					/>
				</FormField>

				<FormField>
					<label className="p-size-m">{format("home.pendingPatientDialog.form.lastName.label")}</label>
					<Controller
						name="lastName"
						control={control}
						rules={{
							required: format("global.form.field.requiredError"),
						}}
						render={({ field, fieldState }) => (
							<>
								<TextInput
									{...field}
									data-test={e2eTestId.createPatientDialog.lastNameTextInput}
									error={isNotNil(fieldState.error)}
									placeholder={format("home.pendingPatientDialog.form.lastName.label")}
									autoCapitalize="words"
								/>
								<ErrorMessage>{error || fieldState.error?.message}</ErrorMessage>
							</>
						)}
					/>
				</FormField>

				<Panel
					variant="compact"
					headerText={format("home.pendingPatientDialog.form.additionalFields")}
					collapsed={true}
				>
					<FormField>
						<label className="p-size-m">{format("home.pendingPatientDialog.form.functionalStatus.label")}</label>
						<Controller
							name="functionalStatus"
							control={control}
							render={({ field }) => (
								<StyledDropdown
									id={e2eTestId.createPatientDialog.dropdownFunctionalStatus}
									options={functionalStatuses.map((type, index) => ({
										id: index,
										value: type,
										label: formatFunctionalStatus(type),
									}))}
									placeholder={format("global.form.dropdown.placeholder")}
									{...field}
									optionLabel="label"
									optionValue="value"
									appendTo="self"
								/>
							)}
						/>
					</FormField>
				</Panel>

				<FormField>
					<Controller
						name="dataConsent"
						control={control}
						rules={{ required: format("home.pendingPatientDialog.form.dataConsent.required") }}
						render={({ field, fieldState }) => (
							<>
								<CheckboxContainer data-test={e2eTestId.createPatientDialog.consentCheckbox}>
									<Checkbox
										style={{ zIndex: 0 }}
										id="data-consent-checkbox"
										inputId="dataConsentCheckbox"
										{...field}
										checked={field.value}
									/>
									<label htmlFor="dataConsentCheckbox">
										{format("home.pendingPatientDialog.form.dataConsent.label")}
									</label>
								</CheckboxContainer>
								<ErrorMessage>{error || fieldState.error?.message}</ErrorMessage>
							</>
						)}
					/>
				</FormField>
			</FormContainer>
			<ButtonContainer>
				<Button uiStyle="secondary" label={format("global.dialog.back")} onClick={onBack} />

				<Button
					data-test={e2eTestId.createPatientDialog.submitButton}
					loading={loadingSubmit}
					label={format("home.pendingPatientDialog.form.submitButton")}
					type="submit"
				/>
			</ButtonContainer>
		</Form>
	);
};

const ButtonContainer = styled.div`
	padding-top: 16px;
	${row("S", "spacebetween")};
`;

const FormField = styled.div`
	${stack("S", "flexstart", "stretch")};
`;

const Form = styled.form`
	${stack("M", "spacebetween", "stretch")};
	height: 100%;
`;
const FormContainer = styled.form`
	${stack("M", "flexstart", "stretch")}
`;

const StyledDropdown = styled(Dropdown)`
	max-width: 330px;
`;

const CheckboxContainer = styled.div`
	${row("S", "flexstart")}
`;
